<template>
  <div>
    <div class="only-on-pdf">
      <!-- Screenshot for PDF export -->
      <img id="map-screenshot" />
    </div>
    <div class="hidden-on-pdf">
      <b-message v-if="validationActivated && !innerZoneGeographique" type="is-danger">
        {{ $t("mapDraw.mandatory-zone-geographique") }}
      </b-message>

      <div v-if="mapVisible" class="MapDrawView-container">
        <l-map
          ref="map"
          class="MapDrawView-map"
          :zoom="initialZoom"
          :center="initialCenter"
          :max-zoom="19"
          :min-zoom="2"
          :dragging="true"
          @ready="mapIsReady"
          @draw:drawstop="stopDraw"
          @draw:created="enableEditPolygon"
          @draw:editstop="updateZoneGeographique"
          @draw:deleted="onDelete"
          @update:zoom="onUpdateZoom"
        >
        </l-map>
        <ProjectDetails
          v-if="showDetails"
          :project="selectedProject"
          :role="role ?? ''"
          :icon="iconDetails ?? ''"
          :user-region-id="userRegionId ?? ''"
          :instructed-graes="instructedGraes"
          @close="closeDetails"
        />
      </div>

      <div class="MapDrawView-legend-container">
        <div class="MapDrawView-legend-grid">
          <div class="MapDrawView-legend">
            <img src="/blueCircle.ico" class="icon" alt="AMENV" style="margin: 5px" />
            {{ $t("mapDraw.legends.AMENV") }}
          </div>
          <div class="MapDrawView-legend">
            <img src="/AME.ico" class="icon" alt="AME" style="margin: 5px" />
            {{ $t("mapDraw.legends.AME") }}
          </div>
          <div class="MapDrawView-legend">
            <img src="/AME_R.ico" class="icon" alt="AME" style="margin: 5px" />
            {{ $t("mapDraw.legends.AME_R") }}
          </div>
          <div class="MapDrawView-legend">
            <img src="/greenCircle.ico" class="icon" alt="ATENV" style="margin: 5px" />
            {{ $t("mapDraw.legends.ATENV") }}
          </div>
          <div class="MapDrawView-legend">
            <img src="/ATE.ico" class="icon" alt="ATE" style="margin: 5px" />
            {{ $t("mapDraw.legends.ATE") }}
          </div>
          <div class="MapDrawView-legend">
            <img src="/ATE_R.ico" class="icon" alt="ATE" style="margin: 5px" />
            {{ $t("mapDraw.legends.ATE_R") }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
// MIGRATION Vue2-Leaflet -> @vue-leaflet/vue-leaflet : Imports simplifiés
// L'initialisation de L, leaflet-draw, leaflet.markercluster et leurs CSS est maintenant gérée globalement dans main.ts
import L, {
  canvas,
  FeatureGroup,
  Icon,
  latLng,
  LatLngBounds,
  LatLng as LatLngType,
  type Control,
} from "leaflet"

import { LMap } from "@vue-leaflet/vue-leaflet"
import { defineComponent } from "vue"
// import regionsGeoJSON from "@/assets/leaflet/regions.geojson?url"
import {
  AireEducativeCartoDetailsDTO,
  AireEducativeCartoDTO,
  CoordinateDTO,
  UtilisateurDTO,
} from "@/model/bean/GeneratedDTOs"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { GraeService } from "@/services/rest/grae/GraeService"
import ProjectDetails from "@/views/maps/ProjectDetails.vue"
import leafletImage from "leaflet-image"

import "mapbox-gl-leaflet/leaflet-mapbox-gl"
import "mapbox-gl/dist/mapbox-gl.css"

// ESM-safe asset imports for Leaflet default marker icons
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png"
import markerIcon from "leaflet/dist/images/marker-icon.png"
import markerShadow from "leaflet/dist/images/marker-shadow.png"
// JSON style (Vite supports JSON imports)
import regionsGeoJSONUrl from "@/assets/leaflet/regions.geojson?url"; // utilisation bundler sûre
import wemapStyle from "@/assets/leaflet/wemapStyle.json"
import { BMessage } from "buefy"
import {
  applyLeafletLocale,
  boundsFromGeoJSON,
  buildDrawnItems,
  isMetropolitanFrance,
  layerToGeoJSONString,
} from "./map-draw-common"

// Debug helper
function dbg(message: string, ...args: any[]) {
  if (process.env.NODE_ENV === "development") console.log(`[MapDebug] ${message}`, ...args)
}

type D = Icon.Default & {
  _getIconUrl?: string
}
delete (Icon.Default.prototype as D)._getIconUrl
Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

export default defineComponent({
  name: "MapDrawWithClusterView",
  components: {
    LMap,
    ProjectDetails,
    BMessage,
  },
  props: {
    zoneGeographique: {
      type: String,
      required: false,
      default: "",
    },
    projets: {
      type: Array,
      required: false,
      default: () => [],
    },
    loggedUser: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    mapHasFocus: {
      type: Boolean,
      required: false,
    },
    group: {
      type: String,
      required: false,
      default: "",
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    guest: {
      type: Boolean,
      required: false,
    },
    role: {
      type: String,
      required: false,
      default: "",
    },
    mapScreenShotRequested: {
      type: Boolean,
      required: false,
    },
    validationActivated: {
      type: Boolean,
      required: false,
      default: false,
    },
    showValidationErrors: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  emits: ["zone-update", "map-screenshot-done"],
  data() {
    return {
      mapVisible: true, // force affichage direct pour éviter carte non montée
      showDetails: false,
      userRegionId: undefined as string | undefined,
      instructedGraes: [] as string[],
      iconDetails: undefined as string | undefined,
      coordinates: new CoordinateDTO(),
      mapInitialized: false,
      initialLoadComplete: false,
      infoReportingService: InfoReportingService.INSTANCE,
      graeService: GraeService.INSTANCE,
      ae: new AireEducativeCartoDTO(),
      selectedProject: new AireEducativeCartoDetailsDTO(),
      drawControl: null as any,
      drawControlEditOnly: null as any,
      // MIGRATION Vue2-Leaflet -> @vue-leaflet/vue-leaflet : Utilisation de L au lieu de window.L
      drawnItems: new L.FeatureGroup() as FeatureGroup,
      innerZoneGeographique: "",
      userRegionName: undefined as string | undefined,
      regionsGeoJSON: null as any,
      mapReadyRetryCount: 0,
      maxMapReadyRetries: 25,
      markersCluster: null as any,
    }
  },
  computed: {
    initialCenter(): [number, number] {
      const long = 0
      const lat = 0
      const initialCenter: [number, number] =
        this.coordinates?.latitude && this.coordinates?.longitude
          ? [this.coordinates.latitude, this.coordinates.longitude]
          : [lat, long]
      dbg("++++++++ initialCenter", initialCenter)
      return initialCenter
    },

    initialZoom(): number {
      let zoom = 2
      if (this.coordinates?.latitude) {
        zoom = this.role === "ENSEIGNANT" ? 8 : 7
      }
      dbg("++++++++ initialZoom", zoom)
      return zoom
    },
  },
  watch: {
    loggedUser(newUser: UtilisateurDTO | undefined, _oldFocus: UtilisateurDTO | undefined): void {
      // @ts-ignore
      this.coordinates = newUser?.coordinates
      // @ts-ignore
      this.userRegionId = this.loggedUser.grae?.id
      this.userRegionName = this.loggedUser?.grae?.region
      this.instructedGraes = this.loggedUser?.instructedGraes ?? []
    },
    mapHasFocus(newFocus: boolean, _oldFocus: boolean): void {
      this.revealMapOnlyIfNeeded(newFocus)
    },
    zoneGeographique(): void {
      this.innerZoneGeographique =
        this.zoneGeographique === "null" ? "" : (this.zoneGeographique ?? "")
    },
    guest(): void {
      this.updateMapCenterAndZoom()
    },
    projets(newvalue: any, oldValue: any): void {
      if (JSON.parse(JSON.stringify(oldValue)).length == 0) {
        this.updateMarkers()
      }
    },
    mapScreenShotRequested(): void {
      this.createMapScreenshot()
    },
    regionsGeoJSON(newVal: any) {
      // @ts-ignore
      const map = (this.$refs.map as any)?.leafletObject || (this.$refs.map as any)?.mapObject
      if (newVal && this.mapInitialized && map) {
        this.addRegionLayers(map)
      }
    },
  },
  created(): void {
    if (this.zoneGeographique && this.zoneGeographique !== "null") {
      this.innerZoneGeographique = this.zoneGeographique
    }
    if (this.loggedUser) {
      this.coordinates = this.loggedUser.coordinates
      this.userRegionId = this.loggedUser.grae?.id
      this.instructedGraes = this.loggedUser.instructedGraes
      this.userRegionName = this.loggedUser.grae?.region
    }
    this.revealMapOnlyIfNeeded(this.mapHasFocus)
    this.initialLoadComplete = true
  },
  async mounted() {
    try {
      const resp = await fetch(regionsGeoJSONUrl)
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      this.regionsGeoJSON = await resp.json()
    } catch (e) {
      console.error("[MapDrawWithClusterView] Error loading regions GeoJSON:", e)
    }
  },
  methods: {
    onZoneChanged(): void {
      this.$emit("zone-update", this.innerZoneGeographique)
    },

    mapIsReady(): void {
      if (this.mapInitialized) {
        dbg("Already initialized, abort")
        return
      }
      // @ts-ignore
      const rawRef: any = this.$refs.map
      dbg("Raw ref keys:", rawRef ? Object.keys(rawRef) : "no ref")
      const map = rawRef?.leafletObject || rawRef?.mapObject
      dbg("Map acquisition", {
        hasLeafletObject: !!rawRef?.leafletObject,
        hasMapObject: !!rawRef?.mapObject,
        mapExists: !!map,
      })
      if (!map) {
        dbg("Map not yet available, retryCount", this.mapReadyRetryCount)
        if (this.mapReadyRetryCount < this.maxMapReadyRetries) {
          this.mapReadyRetryCount++
          setTimeout(() => this.mapIsReady(), 60)
        } else {
          console.error(
            "[MapDrawWithClusterView] L'objet map n'a pas pu être récupéré après plusieurs tentatives",
          )
        }
        return
      }
      // MapboxGL via mapbox-gl-leaflet gère la synchronisation automatiquement
      // Pas besoin d'événements de synchronisation manuels
      // Log container size
      try {
        const el = map.getContainer?.()
        if (el) {
          const cs = getComputedStyle(el)
          dbg("Container size before init", {
            offsetWidth: el.offsetWidth,
            offsetHeight: el.offsetHeight,
            cssWidth: cs.width,
            cssHeight: cs.height,
          })
        }
      } catch {
        console.warn("Error getting container size")
      }
      this.mapReadyRetryCount = 0
      let bounds = new LatLngBounds([])
      try {
        if (this.innerZoneGeographique) {
          dbg("Parsing innerZoneGeographique length", this.innerZoneGeographique.length)
          const zoneGeoParse = JSON.parse(this.innerZoneGeographique)
          dbg("Zone geometry type", zoneGeoParse?.geometry?.type)
          const geoBounds = boundsFromGeoJSON(zoneGeoParse)
          if (geoBounds) bounds = geoBounds
          const canvasRenderer = canvas({ padding: 0.5 })
          this.drawnItems = buildDrawnItems(this.innerZoneGeographique, {
            renderer: canvasRenderer,
          })
          dbg("drawnItems (geoJSON) layer count", this.drawnItems.getLayers().length)
        } else {
          this.drawnItems = new L.FeatureGroup()
          dbg("Empty FeatureGroup created")
        }
        map.addLayer(this.drawnItems)
        applyLeafletLocale((k: string) => this.$t(k).toString())
        if (map.zoomControl) {
          try {
            map.zoomControl.remove()
            dbg("Removed default zoom control")
          } catch {
            console.warn("Error removing default zoom control")
          }
        }
        L.control
          .zoom({
            zoomInTitle: this.$t("mapDraw.zoomin").toString(),
            zoomOutTitle: this.$t("mapDraw.zoomout").toString(),
          })
          .addTo(map)
        dbg("Added custom zoom control")
        this.drawControl = new (L as any).Control.Draw({
          position: "topleft",
          draw: {
            polyline: false,
            polygon: true,
            rectangle: false,
            circle: false,
            marker: false,
            circlemarker: false,
          },
        })
        this.drawControlEditOnly = this.getEditControl()
        dbg("Draw controls prepared", {
          hasDrawControl: !!this.drawControl,
          hasEditOnly: !!this.drawControlEditOnly,
        })
        if (!this.readonly) {
          map.addControl(this.innerZoneGeographique ? this.drawControlEditOnly : this.drawControl)
          dbg("Added control", this.innerZoneGeographique ? "editOnly" : "draw")
        } else dbg("Readonly mode: controls skipped")
        if (this.initialCenter && this.initialZoom) {
          if (this.userRegionName) {
            dbg("Zoom to region", this.userRegionName)
            this.zoomToRegion(this.userRegionName)
          } else {
            map.setView(this.initialCenter, this.initialZoom)
            dbg("Set initial view", this.initialCenter, this.initialZoom)
          }
        } else if (bounds.isValid()) {
          map.fitBounds(bounds, { padding: [4, 4] })
          dbg("Fitted bounds", bounds.toBBoxString())
        } else {
          map.panTo(latLng(46.227, 2.213), 2)
          dbg("Fallback panTo France approx")
        }
      } catch (e) {
        console.error("[MapDrawWithClusterView] Erreur initialisation carte:", e)
        map.panTo(latLng(46.227, 2.213), 2)
      }
      this.addBaseLayer(map)
      setTimeout(() => {
        try {
          map.invalidateSize()
          this.ensureMapHeight(map)
          const el = map.getContainer?.()
          if (el) {
            dbg("Container size after invalidate", { w: el.offsetWidth, h: el.offsetHeight })
          }
        } catch {
          console.warn("Error invalidating map size")
        }
      }, 120)
      this.createMapScreenshot()
      this.updateMarkers()
      if (this.regionsGeoJSON) {
        dbg("Adding region layers after init")
        this.addRegionLayers(map)
      } else dbg("No regionsGeoJSON yet")
      this.mapInitialized = true
      dbg("Map initialization complete")
    },
    addBaseLayer(map: any) {
      dbg("addBaseLayer start")
      if ((map as any)._sagaeBase) {
        try {
          map.removeLayer((map as any)._sagaeBase)
          dbg("Removed previous base layer")
        } catch {
          console.warn("Error removing previous base layer")
        }
      }
      const accessToken = import.meta.env.VUE_APP_MAPBOXGL_ACCESS_TOKEN
      try {
        dbg("Attempting mapboxGL layer, mapboxGL present?", !!(L as any).mapboxGL)
        const mbLayer = (L as any)
          .mapboxGL({
            accessToken,
            style: wemapStyle,
            preserveDrawingBuffer: true,
          })
          .addTo(map)
        ;(map as any)._sagaeBase = mbLayer
        dbg("Mapbox GL base layer added")
      } catch (e) {
        console.warn("[MapDrawWithClusterView] Mapbox GL layer échec, fallback OSM:", e)
        // maxNativeZoom adaptatif : évite "Map data not yet available" dans les régions exotiques
        // Note: OSM est déjà disponible partout, mais on garde la cohérence avec MapDrawView
        const isMetropole = isMetropolitanFrance(this.coordinates?.latitude, this.coordinates?.longitude)
        const maxNative = isMetropole ? 19 : 18
        const osm = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "&copy; OpenStreetMap contributors",
          maxNativeZoom: maxNative,
          maxZoom: 19,
        }).addTo(map)
        ;(map as any)._sagaeBase = osm
        dbg("OSM fallback layer added", { maxNativeZoom: maxNative })
      }
    },
    addRegionLayers(map: any) {
      dbg("addRegionLayers start", this.regionsGeoJSON?.features?.length || 0)
      try {
        this.regionsGeoJSON.features.forEach((f: any, idx: number) => {
          const nom = f.properties.nom
          if (idx < 3) dbg("Region sample", nom)
          L.geoJSON(f, {
            style: { weight: 1, opacity: 0.5, color: "grey", fillOpacity: 0 },
            onEachFeature: (_f, layer) => layer.on("click", () => this.zoomToRegion(nom)),
          }).addTo(map)
        })
      } catch (e) {
        console.warn("[MapDrawWithClusterView] Impossible d'ajouter les régions:", e)
      }
    },
    updateMarkers(): void {
      dbg("updateMarkers start", { projets: (this.projets as AireEducativeCartoDTO[]).length })
      // @ts-ignore
      const rawRef: any = this.$refs.map
      const map = rawRef?.leafletObject || rawRef?.mapObject
      if (!map) {
        dbg("updateMarkers: map absent, abort")
        return
      }
      const bounds = new LatLngBounds([])
      const currentCenter = map.getCenter?.() || latLng(0, 0)
      const currentZoom = map.getZoom?.() || 2
      if (this.markersCluster) {
        try {
          map.removeLayer(this.markersCluster)
        } catch {
          console.warn("Error removing existing cluster layer")
        }
        this.markersCluster.clearLayers()
        dbg("Reusing existing cluster group")
      } else {
        this.markersCluster = L.markerClusterGroup({
          spiderfyOnMaxZoom: true,
          showCoverageOnHover: false,
          spiderLegPolylineOptions: { weight: 0 },
          iconCreateFunction: (cluster: any) => {
            const size = cluster.getChildCount()
            return L.divIcon({
              html: `<div style="position:relative;"><img src="AE.ico" class="cluster-icon-img" style="width:44px;height:44px;transition:width 0.3s, height 0.3s;filter:drop-shadow(1px 2px 2px #222);" onmouseover="this.style.width='52px';this.style.height='52px';" onmouseout="this.style.width='44px';this.style.height='44px';"/><div style="position:absolute;top:-10px;right:-10px;background:white;color:black;border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;">${size}</div></div>`,
              className: "mycluster",
              iconSize: undefined,
            })
          },
        })
        dbg("Created new cluster group")
      }
      let added = 0
      ;(this.projets as AireEducativeCartoDTO[]).forEach((p) => {
        const markerLocation = this.getMarkerLocation(p)
        if (markerLocation.lat !== 0 || markerLocation.lng !== 0) {
          added++
          bounds.extend(markerLocation)
          const iconATE = "/ATE.ico"
          const iconAME = "/AME.ico"
          const iconATER = "/ATE_R.ico"
          const iconAMER = "/AME_R.ico"
          const iconAMENV = "/blueCircle.ico"
          const iconATENV = "/greenCircle.ico"
          let iconpic = "/greenCircle.ico"
          if (p.typeDossier === "DOSSIER_INSCRIPTION") {
            if (
              ["ACCEPTE", "ACCEPTE_SOUS_RESERVE_REFERENT", "AVIS_PASSABLE"].includes(
                p.statutDossier,
              )
            ) {
              iconpic = p.natureAireEducative === "ATE" ? iconATENV : iconAMENV
            }
          } else if (
            p.projetAnnuelIdPrecedent &&
            p.typeDossierPrecedent &&
            p.statutDossierPrecedent &&
            [
              "DOSSIER_LABELLISATION",
              "DOSSIER_RENOUVELLEMENT",
              "DOSSIER_RENOUVELLEMENT_LIGHT",
            ].includes(p.typeDossierPrecedent)
          ) {
            if (
              ["ACCEPTE", "AVIS_PASSABLE", "LABELLISATION_EN_DEVENIR"].includes(
                p.statutDossierPrecedent,
              )
            )
              iconpic = p.natureAireEducative === "ATE" ? iconATER : iconAMER
            else if (
              p.statutDossierPrecedent === "REFUSE" ||
              (p.typeDossierPrecedent === "DOSSIER_LABELLISATION" &&
                p.statutDossierPrecedent === "BROUILLON")
            )
              iconpic = p.natureAireEducative === "ATE" ? iconATENV : iconAMENV
            else if (
              ["DOSSIER_RENOUVELLEMENT", "DOSSIER_RENOUVELLEMENT_LIGHT"].includes(
                p.typeDossierPrecedent,
              ) &&
              p.statutDossierPrecedent === "BROUILLON"
            )
              iconpic = p.natureAireEducative === "ATE" ? iconATE : iconAME
          } else if (p.natureAireEducative === "AME") iconpic = iconAMENV
          const myIcon = L.icon({
            iconUrl: iconpic,
            iconSize: [60, 60],
            iconAnchor: [30, 60],
            className: "marker-pic",
          })
          const bigIcon = L.icon({
            iconUrl: iconpic,
            iconSize: [90, 90],
            iconAnchor: [45, 90],
            className: "marker-pic",
          })
          const marker = L.marker(markerLocation, { alt: p.titre, title: p.titre, icon: myIcon })
          marker.on("mouseover", () => marker.setIcon(bigIcon))
          marker.on("mouseout", () => marker.setIcon(myIcon))
          marker.on("click", this.markerClicked(p, iconpic))
          this.markersCluster.addLayer(marker)
          // polygon hover (optional)
          // @ts-ignore
          if (p.polygone && p.polygone.length > 0) {
            // @ts-ignore
            const polygonCoords = p.polygone.map((coord) => [coord.latitude, coord.longitude])
            const polygon = L.polygon(polygonCoords, {
              color: "#004AAD",
              weight: 2,
              fillColor: "#004AAD",
              fillOpacity: 0.2,
            })
            marker.on("mouseover", () => polygon.addTo(map))
            marker.on("mouseout", () => map.removeLayer(polygon))
          }
        }
      })
      dbg("Markers added", added)
      map.addLayer(this.markersCluster)
      if (bounds.isValid()) {
        map.fitBounds(bounds)
        dbg("Bounds fit after markers", bounds.toBBoxString())
      }
      if (map.setView) map.setView(currentCenter, currentZoom)
      dbg("updateMarkers end (restored view)")
    },

    zoomToRegion(regionName: string): void {
      // @ts-ignore
      const map = this.$refs.map?.mapObject
      // @ts-ignore
      const regionFeature = this.regionsGeoJSON?.features.find(
        (feature: any) => feature.properties.nom === regionName,
      )

      if (regionFeature && map) {
        const bounds = L.geoJSON(regionFeature).getBounds()
        map.fitBounds(bounds, { duration: 1 })
        //const extraZoomLevel = 2; // Ajustez ce niveau de zoom supplémentaire selon vos besoins
        //const currentZoom = map.getZoom();
        //map.setZoom(currentZoom + extraZoomLevel);
      } else {
        console.error(`La région ${regionName} n'a pas été trouvée.`)
      }
    },
    getEditControl(): Control.Draw {
      const anyL = L as any
      if (anyL.EditToolbar?.Delete && !anyL.EditToolbar.Delete.__patched) {
        const original = anyL.EditToolbar.Delete.prototype.enable
        anyL.EditToolbar.Delete.include({
          enable: function (this: any) {
            if (typeof original === "function") original.call(this)
            if (this._externalOnDelete) this._externalOnDelete()
          },
        })
        anyL.EditToolbar.Delete.__patched = true
      }
      if ((L as any).EditToolbar?.Delete)
        (L as any).EditToolbar.Delete.prototype._externalOnDelete = () => this.onDelete()
      return new (L as any).Control.Draw({ edit: { featureGroup: this.drawnItems }, draw: false })
    },
    refreshDrawnItems(): void {
      // @ts-ignore
      const rawRef: any = this.$refs.map
      const map = rawRef?.leafletObject || rawRef?.mapObject
      if (!map) return
      map.removeLayer(this.drawnItems)
      // @ts-ignore
      this.drawnItems = new L.FeatureGroup()
      map.addLayer(this.drawnItems)
    },

    enableEditPolygon(e: any): void {
      // @ts-ignore
      const map = this.$refs.map.mapObject
      this.drawnItems.addLayer(e.layer)
      map.removeControl(this.drawControl)
      this.refreshDrawnControlEditOnly()
      // Mise à jour du GeoJSON via utilitaire
      const json = layerToGeoJSONString(e.layer)
      if (json) {
        this.innerZoneGeographique = json
        this.onZoneChanged()
      }
    },

    refreshDrawnControlEditOnly(): void {
      // @ts-ignore
      const map = this.$refs.map.mapObject
      this.drawControlEditOnly = this.getEditControl()
      map.addControl(this.drawControlEditOnly)
    },

    markerClicked(projet: AireEducativeCartoDTO, icon: string): () => void {
      return async () => {
        this.iconDetails = icon
        this.ae = projet
        this.showDetails = true
        this.selectedProject = await this.graeService.getProjectDetails(projet.projetAnnuelId)
      }
    },

    closeDetails(): void {
      this.showDetails = false
      this.ae = new AireEducativeCartoDTO()
    },

    revealMapOnlyIfNeeded(reveal: boolean): void {
      if (!this.mapVisible && reveal) {
        this.$nextTick(() => {
          this.mapVisible = true
        })
      }
    },

    createMapScreenshot(): void {
      // @ts-ignore
      const map = this.$refs.map.mapObject
      if (this.mapScreenShotRequested) {
        console.info("Map Screenshot request...")
        leafletImage(map, (err: any, canvas: any) => {
          let dataURL
          if (!err) {
            dataURL = canvas.toDataURL()
          } else {
            console.error("Error while taking map screenshot")
          }
          this.$emit("map-screenshot-done", dataURL)
        })
      }
    },

    stopDraw(event: any): void {
      dbg("Draw stopped", event)
    },

    updateZoneGeographique(event: any): void {
      // Handler pour draw:editstop (mise à jour du GeoJSON)
      if (!event || !event.target || !event.target._layers) return
      Object.values(event.target._layers).forEach((layer: any) => {
        if (layer._latlngs && layer._latlngs.length !== 0) {
          const json = layerToGeoJSONString(layer)
          if (json) {
            this.innerZoneGeographique = json
            this.onZoneChanged()
          }
        }
      })
    },

    onUpdateZoom(newZoom: number): void {
      dbg(" -------------- Zoom level changed", newZoom)
    },

    onDelete(): void {
      this.infoReportingService.dialog(
        this.$t("usersList.confirmation").toString(),
        this.$t("mapDraw.delete-draw").toString(),
        this.$t("mapDraw.delete").toString(),
        () => this.deleteAireAndEnableDrawControl(),
        "is-warning",
      )
    },

    deleteAireAndEnableDrawControl(): void {
      this.refreshDrawnItems()
      // @ts-ignore
      const map = this.$refs.map.mapObject

      if (this.drawnItems.getLayers().length === 0) {
        map.removeControl(this.drawControlEditOnly)
        map.addControl(this.drawControl)
        this.innerZoneGeographique = ""
        this.onZoneChanged()
      }
    },

    updateMapCenterAndZoom(): void {
      // @ts-ignore
      const map = this.$refs.map.mapObject
      if (map) {
        map.setView(this.initialCenter, this.initialZoom)
      }
    },
    getMarkerLocation(projet: AireEducativeCartoDTO): LatLngType {
      if (projet.latitude && projet.longitude) {
        return latLng(projet.latitude, projet.longitude)
      }
      return latLng(0, 0)
    },
    ensureMapHeight(map: any) {
      try {
        const el = map.getContainer?.()
        if (el) {
          if (el.offsetHeight === 0) {
            dbg("Forcing map container height (was 0)")
            el.style.minHeight = "600px"
            el.style.height = "600px"
            el.style.display = "block"
            setTimeout(() => {
              try {
                map.invalidateSize()
              } catch {
                console.warn("Error invalidating map size after height fix")
              }
            }, 50)
          }
        }
      } catch {
        console.warn("Error ensuring map height")
      }
    },
  },
})
</script>

<style lang="scss">
@import "../../../node_modules/bulma/sass/utilities/_all.sass";
@import "@/styles/variables.scss";

// Correction: l'élément <l-map> a la classe .MapDrawView-map et le conteneur interne a .leaflet-container
// L'ancien sélecteur &.leaflet-container ne s'appliquait jamais.
.MapDrawView-map {
  position: relative;
  width: 100%;
  min-height: 600px; // hauteur minimale pour l'initialisation
  height: 50em;
  display: block;
  > .leaflet-container {
    width: 100% !important;
    min-height: 600px;
    height: 100%;
  }
}

@media screen and (max-width: $fullhd) {
  .MapDrawView-map {
    width: 100%;
    > .leaflet-container {
      width: 100%;
    }
  }
}

.MapDrawView-container {
  .leaflet-draw-tooltip-single {
    display: none;
  }
}

.MapDrawView-legend-container {
  margin-top: 1.5rem;
  width: 100% !important;
  max-width: 100% !important;
}

.MapDrawView-legend {
  display: flex;
  align-items: center;
}

.MapDrawView-invisible-input {
  .InputWithValidation-field {
    display: none;
  }
}

.details-ae {
  width: 480px;
  padding: 10px;
}

.marker-pic::after {
  content: " ";
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 37%;
  top: 74%;
  background: radial-gradient(
    circle,
    rgba(0, 0, 0, 0.2) 0,
    rgba(0, 0, 0, 0.08) 25%,
    transparent 50%
  );
  transform: rotateX(70deg);
}

.mycluster {
  height: 40px;
  width: 40px;
  border-radius: 50%;
  font-size: 8px;
  margin-top: -40px;
  margin-left: -20px;
}

.marker-pic {
  opacity: 1;
  -webkit-filter: drop-shadow(1px 2px 1px #222);
  filter: drop-shadow(1px 2px 2px #222);
}

.projet_detail {
  h2 {
    font-size: 20px;
    font-weight: bolder;
  }

  font-size: 16px;
}

.b-table .item {
  padding: 20px;
  box-sizing: border-box;
  border-bottom: 1px solid #eee;
}

.projet_detail p {
  margin: 5px 0;
}

.projet_detail strong {
  font-weight: bold;
}

// MIGRATION Vue2-Leaflet -> @vue-leaflet/vue-leaflet : Suppression du style vide
// .marker-pic:hover {
//   //width: 45px !important;
//   // height: 45px !important;
// }

.modal-card-foot {
  display: flex;
  justify-content: flex-end;
}

.buttons .hoverable-discrete-link {
  cursor: pointer;
  color: #007bff;
  text-decoration: underline;
}

.MapDrawView-legend-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.MapDrawView-legend img.icon {
  width: 24px;
  height: 24px;
  margin-right: 10px;
}
</style>
